/**
 * Extraction Overview Card
 *
 * Top-level stats banner showing pipeline health: passes, confidence,
 * completeness, and entity counts at a glance.
 */

import React from 'react';
import { View, Text } from 'react-native';
import {
  Activity,
  CheckCircle2,
  XCircle,
  Layers,
  BarChart3,
  Users,
  Building2,
  LineChart,
} from 'lucide-react-native';
import Animated, { FadeIn } from 'react-native-reanimated';
import type { RichExtraction } from '@/lib/supabase/edge-functions';
import { useThemeColors } from '@/lib/useThemeColors';

interface ExtractionOverviewCardProps {
  extraction: RichExtraction;
}

export function ExtractionOverviewCard({ extraction }: ExtractionOverviewCardProps) {
  const colors = useThemeColors();
  const { quality, data, entities, metadata } = extraction;

  const confidenceColor = quality.overallConfidence >= 70 ? colors.success
    : quality.overallConfidence >= 40 ? colors.warning
    : colors.error;
  const confidenceBg = quality.overallConfidence >= 70 ? colors.traffic.greenLight
    : quality.overallConfidence >= 40 ? colors.amber.light
    : colors.red.light;

  const allPassed = quality.passesSucceeded === quality.passesAttempted;
  const completenessPercent = Math.round(quality.dataCompleteness * 100);

  const statItems = [
    { icon: BarChart3, label: 'Metrics', value: data.metrics.length, color: colors.primary[600] },
    { icon: LineChart, label: 'Charts', value: data.charts.length, color: colors.primary[600] },
    { icon: Users, label: 'People', value: entities.people.length, color: colors.primary[600] },
    { icon: Building2, label: 'Companies', value: entities.companies.length, color: colors.primary[600] },
  ];

  return (
    <Animated.View entering={FadeIn.duration(400)}>
      <View
        className="rounded-2xl overflow-hidden"
        style={{
          backgroundColor: colors.surface.primary,
          borderWidth: 1,
          borderColor: colors.border.light,
        }}
      >
        {/* Top row: Confidence + Pass status */}
        <View className="p-4 pb-3">
          <View className="flex-row items-center justify-between mb-3">
            <View className="flex-row items-center gap-2">
              <Activity size={14} color={colors.text.tertiary} />
              <Text style={{ color: colors.text.tertiary }} className="text-xs font-medium uppercase tracking-wider">
                Multi-Pass Extraction
              </Text>
            </View>
            <View className="flex-row items-center gap-1.5">
              <Text style={{ color: colors.text.tertiary }} className="text-xs">
                v{metadata.pipelineVersion}
              </Text>
            </View>
          </View>

          <View className="flex-row items-center gap-4">
            {/* Confidence score */}
            <View className="items-center">
              <View
                className="w-14 h-14 rounded-xl items-center justify-center mb-1"
                style={{ backgroundColor: confidenceBg }}
              >
                <Text style={{ color: confidenceColor }} className="text-xl font-bold">
                  {quality.overallConfidence}
                </Text>
              </View>
              <Text style={{ color: colors.text.tertiary }} className="text-xs">
                confidence
              </Text>
            </View>

            {/* Pass status + completeness */}
            <View className="flex-1 gap-2">
              <View className="flex-row items-center gap-2">
                {allPassed ? (
                  <CheckCircle2 size={14} color={colors.success} />
                ) : (
                  <XCircle size={14} color={colors.error} />
                )}
                <Text style={{ color: allPassed ? colors.success : colors.error }} className="text-xs font-semibold">
                  {quality.passesSucceeded}/{quality.passesAttempted} passes succeeded
                </Text>
              </View>

              <View className="flex-row items-center gap-2">
                <Layers size={14} color={colors.text.tertiary} />
                <Text style={{ color: colors.text.secondary }} className="text-xs">
                  {completenessPercent}% data completeness
                </Text>
              </View>

              {/* Completeness bar */}
              <View className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: colors.surface.tertiary }}>
                <View
                  className="h-full rounded-full"
                  style={{
                    width: `${completenessPercent}%`,
                    backgroundColor: completenessPercent >= 70 ? colors.success
                      : completenessPercent >= 40 ? colors.warning
                      : colors.error,
                  }}
                />
              </View>
            </View>
          </View>
        </View>

        {/* Stat pills row */}
        <View
          className="flex-row px-4 py-3 gap-2"
          style={{ borderTopWidth: 1, borderTopColor: colors.border.light }}
        >
          {statItems.map(({ icon: Icon, label, value, color }) => (
            <View
              key={label}
              className="flex-1 flex-row items-center justify-center gap-1.5 py-1.5 rounded-lg"
              style={{ backgroundColor: colors.surface.secondary }}
            >
              <Icon size={12} color={value > 0 ? color : colors.text.tertiary} />
              <Text
                style={{ color: value > 0 ? colors.text.primary : colors.text.tertiary }}
                className="text-xs font-bold"
              >
                {value}
              </Text>
              <Text style={{ color: colors.text.tertiary }} className="text-xs">
                {label}
              </Text>
            </View>
          ))}
        </View>
      </View>
    </Animated.View>
  );
}
